import { Injectable } from '@angular/core';
import { Row } from '@components/html-editor/table-generator-dialog/table-generator.model';
import { RowTypes } from '@components/html-editor/table-generator-dialog/table-generator.util';

@Injectable({
	providedIn: 'root',
})
export class TableGeneratorService {
	/**
	 * Merge the cell to the right of the selected column in a specified row.
	 * @param rowType - The type of the row (header, body, footer).
	 * @param selectedColumn - The index of the selected column.
	 * @param numOfColumns - The total number of columns in the table.
	 * @param rows - The array of rows in the table.
	 * @param selectedRowIndex - The index of the selected row.
	 */
	mergeRight(
		rowType: string,
		selectedColumn: number,
		numOfColumns: number,
		rows: Row[],
		selectedRowIndex: number,
	): void {
		// Only perform the merge operation for header, body, or footer rows
		if (
			rowType === RowTypes.HEADER ||
			rowType === RowTypes.BODY ||
			rowType === RowTypes.FOOTER
		) {
			// Check if selectedColumn is out of range
			if (
				selectedColumn >= rows[selectedRowIndex].cells.length ||
				selectedColumn < 0
			) {
				return; // Do nothing if selectedColumn is out of range
			}

			// Check if selectedColumn is the last column
			if (selectedColumn === rows[selectedRowIndex].cells.length - 1) {
				return; // Do nothing if selectedColumn is the last column
			}

			// Check if the colspan limit is reached
			if (
				rows[selectedRowIndex].cells[selectedColumn].colspan >= numOfColumns
			) {
				return; // Do nothing if the colspan limit is reached
			}

			// Increment the colspan of the selected cell and remove the next cell
			rows[selectedRowIndex].cells[selectedColumn].colspan += 1;
			rows[selectedRowIndex].cells.splice(selectedColumn + 1, 1);
		}
	}

	/**
	 * Adds a new column to the header, body, and footer rows.
	 *
	 * @param {Row} headerRow - The header row object.
	 * @param {Row[]} bodyRows - An array of body row objects.
	 * @param {Row[]} footerRows - An array of footer row objects.
	 * @param {number[]} columnWidths - An array of column widths.
	 *
	 * @returns {Object} - The updated header, body, and footer rows, and column widths.
	 */
	private addColumn(
		headerRow: Row,
		bodyRows: Row[],
		footerRows: Row[],
		columnWidths: number[],
	): {
		headerRow: Row;
		bodyRows: Row[];
		footerRows: Row[];
		columnWidths: number[];
	} {
		headerRow.cells.push({ text: '', colspan: 1 });
		bodyRows.forEach((row) => {
			row.cells.push({ text: '', colspan: 1 });
		});
		footerRows.forEach((row) => {
			row.cells.push({ text: '', colspan: 1 });
		});

		const newColumnWidths: number[] = this.calculateNewColumnWidths(
			columnWidths,
			columnWidths.length + 1,
		);
		columnWidths = newColumnWidths;

		return { headerRow, bodyRows, footerRows, columnWidths };
	}

	/**
	 * Removes a column from the header, body, and footer rows based on the number of columns.
	 *
	 * @param {number} columns - The number of columns to keep.
	 * @param {Row} headerRow - The header row object.
	 * @param {Row[]} bodyRows - An array of body row objects.
	 * @param {Row[]} footerRows - An array of footer row objects.
	 * @param {number[]} columnWidths - An array of column widths.
	 *
	 * @returns {Object} - The updated header, body, and footer rows, and column widths.
	 */
	private removeColumn(
		columns: number,
		headerRow: Row,
		bodyRows: Row[],
		footerRows: Row[],
		columnWidths: number[],
	): {
		headerRow: Row;
		bodyRows: Row[];
		footerRows: Row[];
		columnWidths: number[];
	} {
		const adjustRow = (row: Row) => {
			const currentColSpanSum = row.cells.reduce(
				(sum, cell) => sum + cell.colspan,
				0,
			);

			//Means some cells have been merged
			if (row.cells.length < currentColSpanSum) {
				if (row.cells[row.cells.length - 1].colspan > 1) {
					row.cells[row.cells.length - 1].colspan -= 1;

					return;
				}
			}

			//Remove last cell
			row.cells.splice(row.cells.length - 1, 1);
		};

		adjustRow(headerRow);
		bodyRows.forEach(adjustRow);
		footerRows.forEach(adjustRow);

		const newColumnWidths: number[] = columnWidths
			.map((width) => Number(width))
			.splice(0, columns);
		const sumOfNewWidths = newColumnWidths.reduce((a, b) => a + b, 0);
		newColumnWidths[newColumnWidths.length - 1] =
			newColumnWidths[newColumnWidths.length - 1] + (100 - sumOfNewWidths);

		return { headerRow, bodyRows, footerRows, columnWidths: newColumnWidths };
	}

	/**
	 * Calculates the new column widths after adding or removing columns.
	 *
	 * @param {number[]} currentColumnWidths - An array of column widths.
	 * @param {number} newNumberOfColumns - The number of columns to keep.
	 *
	 * @returns {number[]} - The new array of column widths.
	 */
	private calculateNewColumnWidths(
		currentColumnWidths: number[],
		newNumberOfColumns: number,
	): number[] {
		let newColumnWidths = [...currentColumnWidths]; // clone the current column widths

		if (currentColumnWidths.length === 0) {
			// When there are no current column widths, split the width evenly across the new columns
			return new Array(newNumberOfColumns).fill(100 / newNumberOfColumns);
		}

		if (newNumberOfColumns > currentColumnWidths.length) {
			const lastColumnWidth =
				currentColumnWidths[currentColumnWidths.length - 1];
			for (let i = currentColumnWidths.length; i < newNumberOfColumns; i++) {
				newColumnWidths.push(lastColumnWidth);
			}
		} else {
			newColumnWidths = currentColumnWidths.slice(0, newNumberOfColumns);
		}

		// Calculate the total sum of the current widths.
		const totalWidth = newColumnWidths.reduce((acc, width) => acc + width, 0);

		// Normalize the widths so that they add up to 100.
		newColumnWidths = newColumnWidths.map(
			(width) => (width / totalWidth) * 100,
		);

		return newColumnWidths;
	}

	/**
	 * Updates the number of columns in the table. It either adds or removes columns based on the provided number of columns.
	 *
	 * @param {number} columns - The new number of columns.
	 * @param {number} numOfColumns - The current number of columns.
	 * @param {Row} headerRow - The header row object.
	 * @param {Row[]} bodyRows - An array of body row objects.
	 * @param {Row[]} footerRows - An array of footer row objects.
	 * @param {number[]} columnWidths - An array of column widths.
	 *
	 * @returns {Object} - The updated number of columns, header, body, and footer rows, and column widths.
	 */
	updateNumberOfColumns(
		columns: number,
		numOfColumns: number,
		headerRow: Row,
		bodyRows: Row[],
		footerRows: Row[],
		columnWidths: number[],
	): {
		numOfColumns: number;
		headerRow: Row;
		bodyRows: Row[];
		footerRows: Row[];
		columnWidths: number[];
	} {
		let updatedTable: {
			headerRow: Row;
			bodyRows: Row[];
			footerRows: Row[];
			columnWidths: number[];
		};

		if (columns > numOfColumns) {
			updatedTable = this.addColumn(
				headerRow,
				bodyRows,
				footerRows,
				columnWidths,
			);
		} else {
			updatedTable = this.removeColumn(
				columns,
				headerRow,
				bodyRows,
				footerRows,
				columnWidths,
			);
		}

		numOfColumns = columns;

		return { numOfColumns, ...updatedTable };
	}
}
